import React, { useCallback, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useStaticQuery, graphql, Slice, navigate } from "gatsby";
import Header from "./header";
import classNames from "classnames";
import useScrolledHeader from "../hooks/use-scroll-header";
import useClickOut from "../hooks/use-click-out";
import HeaderEpp from "./header-epp";
import HeaderSimple from "./header-simple";
import LanguageModal from "./language-modal";
import NewHeader from "./new-header/new-header";

const Layout = ({
  bannerRef,
  bannerTop,
  hideMainBenefits,
  isEppMenu,
  hasSimpleMenu,
  customFooter,
  isAnchor,
  mainBenefits,
  generatedLocale,
  newMenu,
  newCustomFooter,
  newCustomPaymentFooter,
  newCustomSocialFooter,
  isDark,
  customLogo,
  children,
}) => {
  const { site, labelManagerLabels } = useStaticQuery(
    graphql`
      query SiteTitleQuery {
        site {
          siteMetadata {
            title
          }
        }
        labelManagerLabels: labelManagerLabels {
          notEnabledJavaScript
        }
      }
    `
  );
  const [headerRef, hasScrolled, navUp, stickMenus] = useScrolledHeader();
  const [hamburger, setHamburger] = useState(false);
  const toggleHamburger = useCallback(() => setHamburger((value) => !value), []);
  const hamburgerRef = useClickOut(hamburger, setHamburger);
  const isBrowser = () => typeof window !== "undefined";
  const [searchVisible, setSearchVisible] = useState(() =>
    new URLSearchParams(isBrowser() && window.location.hash?.slice(1)).get("search")?.trim()
  );
  useEffect(() => {
    if (!searchVisible) {
      const hash = new URLSearchParams(window.location.hash?.slice(1));
      if (!!hash.get("search")?.trim()) {
        const searchString = window.location.search;
        hash.delete("search");
        const hashString = hash.toString();
        navigate(
          `${searchString || hashString ? "" : "?"}${searchString}${
            hashString ? `#${hashString}` : ""
          }`
        );
      }
    }
  }, [searchVisible]);

  const [headerHeight, setHeaderHeight] = useState();
  useEffect(() => {
    setHeaderHeight(headerRef?.current?.getBoundingClientRect()?.height);
  }, [headerRef, navUp]);

  const [headerListHeight, setHeaderListHeight] = useState();
  useEffect(() => {
    const listener = (e) => {
      setHeaderListHeight(e.detail.height);
    };
    document.addEventListener("header-resize", listener);
    return () => document.removeEventListener("header-resize", listener);
  }, []);

  const [bannerTopMenuHeight, setBannerTopMenuHeight] = useState();
  useEffect(() => {
    const listener = (e) => {
      setBannerTopMenuHeight(e.detail.height);
    };
    document.addEventListener("banner-top-menu-resize", listener);
    return () => document.removeEventListener("banner-top-menu-resize", listener);
  }, []);

  const [appHeight, setAppHeight] = useState();
  useEffect(() => {
    const appHeight = () => {
      setAppHeight(
        window.visualViewport?.height
          ? Math.min(window.innerHeight, window.visualViewport.height)
          : window.innerHeight
      );
    };
    window.addEventListener("resize", appHeight);
    window.visualViewport?.addEventListener("resize", appHeight);
    appHeight();
    return () => {
      window.removeEventListener("resize", appHeight);
      window.visualViewport?.removeEventListener("resize", appHeight);
    };
  }, []);

  return (
    <main
      className={classNames({
        "hasScrolled": hasScrolled,
        "navUp": navUp,
        "navDown": !navUp,
        "hasScrolled--stickMenus": stickMenus,
        "has--hideMainBenefits": hideMainBenefits && hideMainBenefits === "yes",
        "has--eppMenu": isEppMenu,
        "menuMobileVisible": hamburger,
        "hasBannerTop": bannerTop && bannerTopMenuHeight !== 0,
        "has--anchor": isAnchor,
      })}
      style={{
        "--banner-top-height":
          bannerTop && bannerTopMenuHeight ? `${bannerTopMenuHeight}px` : "0px",
        "--header-height": headerHeight ? `${Math.floor(headerHeight)}px` : undefined,
        "--header-list-height": headerListHeight ? `${Math.floor(headerListHeight)}px` : undefined,
        "--app-height": appHeight ? `${Math.floor(appHeight)}px` : undefined,
      }}
    >
      <div className={classNames("wrapper", isDark && "dark")}>
        {isEppMenu ? (
          <HeaderEpp />
        ) : hasSimpleMenu ? (
          <HeaderSimple
            siteTitle={site.siteMetadata.title}
            ref={headerRef}
            hideMainBenefits={hideMainBenefits}
            mainBenefits={mainBenefits}
            bannerTop={bannerTop}
            bannerRef={bannerRef}
            customLogo={customLogo}
          />
        ) : newMenu ? (
          <NewHeader
            hideMainBenefits={hideMainBenefits}
            mainBenefits={mainBenefits}
            bannerRef={bannerRef}
            bannerTop={bannerTop}
            toggleHamburger={toggleHamburger}
            hamburgerRef={hamburgerRef}
            mobileMenuVisible={hamburger}
            siteTitle={site.siteMetadata.title}
            navUp={navUp}
            ref={headerRef}
            searchVisible={searchVisible}
            setSearchVisible={setSearchVisible}
            bannerTopMenuHeight={bannerTopMenuHeight}
            hamburger={hamburger}
          />
        ) : (
          <Header
            hideMainBenefits={hideMainBenefits}
            mainBenefits={mainBenefits}
            bannerRef={bannerRef}
            bannerTop={bannerTop}
            toggleHamburger={toggleHamburger}
            hamburgerRef={hamburgerRef}
            mobileMenuVisible={hamburger}
            siteTitle={site.siteMetadata.title}
            hasScrolled={hasScrolled}
            ref={headerRef}
            searchVisible={searchVisible}
            setSearchVisible={setSearchVisible}
            bannerTopMenuHeight={bannerTopMenuHeight}
            headerHeight={headerHeight}
          />
        )}

        {children}
        {isEppMenu ? (
          <Slice alias="site-footer-epp" />
        ) : hasSimpleMenu && !customFooter && !newMenu ? (
          <Slice
            alias="site-footer-simple"
            generatedLocale={generatedLocale}
            customLogo={customLogo}
          />
        ) : hasSimpleMenu && !newCustomFooter && newMenu ? (
          <Slice
            alias="site-new-footer-simple"
            generatedLocale={generatedLocale}
            customLogo={customLogo}
          />
        ) : newMenu ? (
          <Slice
            alias="site-new-footer"
            generatedLocale={generatedLocale}
            newCustomFooter={newCustomFooter}
            newCustomSocialFooter={newCustomSocialFooter}
            newCustomPaymentFooter={newCustomPaymentFooter}
          />
        ) : (
          <Slice
            alias="site-footer"
            customFooter={customFooter}
            generatedLocale={generatedLocale}
          />
        )}
        <LanguageModal />
        <noscript>
          <div className="nojs-msg text-center">{labelManagerLabels.notEnabledJavaScript}</div>
        </noscript>
      </div>
    </main>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Layout;
